import React from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Help as HelpIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

const getRecommendationColor = (recommendation) => {
  switch (recommendation) {
    case 'INTERVIEW':
      return 'success';
    case 'REJECT':
      return 'error';
    case 'ASK_QUESTIONS':
      return 'info';
    case 'SKIPPING':
      return 'warning';
    case 'REQUIRES_HUMAN':
      return 'secondary';
    default:
      return 'default';
  }
};

const getRecommendationIcon = (recommendation) => {
  switch (recommendation) {
    case 'INTERVIEW':
      return <CheckCircleIcon />;
    case 'REJECT':
      return <CancelIcon />;
    case 'ASK_QUESTIONS':
      return <HelpIcon />;
    case 'SKIPPING':
      return <WarningIcon />;
    case 'REQUIRES_HUMAN':
      return <ScheduleIcon />;
    default:
      return <PersonIcon />;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return 'Unknown';
  return new Date(dateString).toLocaleString();
};

const AutomationStatus = ({ 
  selectedJob, 
  autoReplyConfig, 
  setCurrentStep, 
  handleStopAutomation,
  candidateAnalyses = [],
  automationStats = {},
  isAutomationRunning = false
}) => {
  const progress = automationStats.totalMessages > 0 
    ? (automationStats.currentMessage / automationStats.totalMessages) * 100 
    : 0;

  return (
    <Box sx={{ 
      width: '100%', 
      padding: 2,
      boxSizing: 'border-box'
    }}>
      {/* Main Status Paper */}
      <Paper elevation={3} sx={{ padding: 4, mb: 3 }}>
        <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
          {isAutomationRunning ? 'Automation Status' : 'Automation Results'}
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 2 }}>
          {isAutomationRunning 
            ? <>Automation is running for: <strong>{selectedJob?.title}</strong></> 
            : <>Automation completed for: <strong>{selectedJob?.title}</strong></>
          }
        </Typography>
        
        {/* Progress Bar */}
        {automationStats.totalMessages > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">
                Processing messages: {automationStats.currentMessage}/{automationStats.totalMessages}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.round(progress)}%
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        )}

        {/* Automation Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" color="success.main">
                  {automationStats.interviewed || 0}
                </Typography>
                <Typography variant="caption">Interviews</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" color="error.main">
                  {automationStats.rejected || 0}
                </Typography>
                <Typography variant="caption">Rejected</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" color="info.main">
                  {automationStats.questioned || 0}
                </Typography>
                <Typography variant="caption">Questions</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" color="warning.main">
                  {automationStats.skipped || 0}
                </Typography>
                <Typography variant="caption">Skipped</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" color="secondary.main">
                  {automationStats.requiresHuman || 0}
                </Typography>
                <Typography variant="caption">Human Review</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" color="text.secondary">
                  {automationStats.errors || 0}
                </Typography>
                <Typography variant="caption">Errors</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Configuration Details */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>Automation Configuration</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Success Message:</strong> {autoReplyConfig.firstMessage}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Calendly Link:</strong> {autoReplyConfig.calendlyLink}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>No CV Message:</strong> {autoReplyConfig.noCvMessage || 
              "Could you please share your CV? I'd like to review your experience and discuss potential opportunities."}
            </Typography>
          </AccordionDetails>
        </Accordion>
        
        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleStopAutomation}
            sx={{ width: '200px', mx: 'auto' }}
            disabled={!isAutomationRunning}
          >
            {isAutomationRunning ? 'Stop Automation' : 'Automation Stopped'}
          </Button>
        </Box>
      </Paper>

      {/* Candidate Analyses */}
      {candidateAnalyses.length > 0 && (
        <Paper elevation={3} sx={{ padding: 4 }}>
          <Typography component="h2" variant="h6" sx={{ mb: 3 }}>
            Processed Candidates ({candidateAnalyses.length})
          </Typography>
          
          <List>
            {candidateAnalyses.map((analysis, index) => (
              <React.Fragment key={analysis.id}>
                <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: getRecommendationColor(analysis.recommendation?.recommendation) + '.main' }}>
                      {getRecommendationIcon(analysis.recommendation?.recommendation)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle1" component="span">
                          {analysis.person?.name || 'Unknown Candidate'}
                        </Typography>
                        {analysis.recommendation?.recommendation && (
                          <Chip 
                            label={analysis.recommendation.recommendation.replace('_', ' ')} 
                            color={getRecommendationColor(analysis.recommendation.recommendation)}
                            size="small"
                          />
                        )}
                        {analysis.recommendation?.score && (
                          <Chip 
                            label={`Score: ${analysis.recommendation.score}%`} 
                            variant="outlined"
                            size="small"
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        {/* Person Details */}
                        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                            👤 Person Details
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                                <strong>Position:</strong> {analysis.person?.jobPosition || 'Unknown'}
                              </Typography>
                              <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                                <strong>Message Date:</strong> {formatDate(analysis.person?.messageDate)}
                              </Typography>
                              {analysis.person?.candidateId && (
                                <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                                  <strong>Candidate ID:</strong> {analysis.person.candidateId}
                                </Typography>
                              )}
                              {analysis.person?.pageNumber && (
                                <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                                  <strong>Found on Page:</strong> {analysis.person.pageNumber}
                                </Typography>
                              )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              {analysis.conversationHistory && (
                                <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                                  <strong>Previous Messages:</strong> {analysis.conversationHistory.messageCount || 0}
                                </Typography>
                              )}
                              {analysis.person?.profileUrl && (
                                <Box sx={{ mb: 0.5 }}>
                                  <Button 
                                    size="small" 
                                    variant="outlined" 
                                    href={analysis.person.profileUrl} 
                                    target="_blank"
                                    sx={{ mr: 1 }}
                                  >
                                    View Profile
                                  </Button>
                                </Box>
                              )}
                              {analysis.person?.conversationUrl && (
                                <Box sx={{ mb: 0.5 }}>
                                  <Button 
                                    size="small" 
                                    variant="outlined" 
                                    href={analysis.person.conversationUrl} 
                                    target="_blank"
                                  >
                                    View Conversation
                                  </Button>
                                </Box>
                              )}
                            </Grid>
                          </Grid>
                          {analysis.person?.messagePreview && (
                            <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                Message Preview:
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                "{analysis.person.messagePreview}"
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* Questionnaire Responses */}
                        {analysis.questionnaire && analysis.questionnaire.length > 0 && (
                          <Box sx={{ mb: 2, p: 2, bgcolor: 'warning.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" color="warning.main" sx={{ mb: 1 }}>
                              📋 Candidate Questionnaire Responses ({analysis.questionnaire.length})
                            </Typography>
                            
                            {analysis.questionnaire.map((item, qIndex) => (
                              <Box key={qIndex} sx={{ mb: 2, p: 1.5, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="body2" color="text.primary" sx={{ mb: 1, fontWeight: 'medium' }}>
                                  <strong>Q:</strong> {item.question}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ pl: 2, borderLeft: '3px solid', borderColor: 'warning.main', fontStyle: 'italic' }}>
                                  <strong>A:</strong> {item.answer}
                                </Typography>
                              </Box>
                            ))}
                            
                            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                              💡 This structured data was used by AI to make a more informed recommendation
                            </Typography>
                          </Box>
                        )}

                        {/* AI Recommendation Details */}
                        {analysis.recommendation && (
                          <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                            <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                              🤖 AI Analysis & Recommendation
                            </Typography>
                            
                            {analysis.recommendation.reasoning && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                <strong>Reasoning:</strong> {analysis.recommendation.reasoning}
                              </Typography>
                            )}

                            {analysis.recommendation.followUpMessage && (
                              <Box sx={{ mb: 1, p: 1, bgcolor: 'info.50', borderRadius: 1 }}>
                                <Typography variant="caption" color="info.main" sx={{ display: 'block', mb: 0.5 }}>
                                  AI Generated Message:
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  "{analysis.recommendation.followUpMessage}"
                                </Typography>
                              </Box>
                            )}

                            {/* Skills Analysis */}
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                              {analysis.recommendation.keySkills?.length > 0 && (
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="caption" color="success.main" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>
                                    ✅ Key Skills Found:
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {analysis.recommendation.keySkills.map((skill, skillIndex) => (
                                      <Chip key={skillIndex} label={skill} size="small" color="success" variant="outlined" />
                                    ))}
                                  </Box>
                                </Grid>
                              )}
                              
                              {analysis.recommendation.missingSkills?.length > 0 && (
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>
                                    ⚠️ Missing Skills:
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {analysis.recommendation.missingSkills.map((skill, skillIndex) => (
                                      <Chip key={skillIndex} label={skill} size="small" color="warning" variant="outlined" />
                                    ))}
                                  </Box>
                                </Grid>
                              )}
                            </Grid>

                            {/* Strengths and Weaknesses */}
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                              {analysis.recommendation.strengths?.length > 0 && (
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="caption" color="success.main" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>
                                    💪 Strengths:
                                  </Typography>
                                  <List dense>
                                    {analysis.recommendation.strengths.map((strength, index) => (
                                      <ListItem key={index} sx={{ py: 0, px: 1 }}>
                                        <Typography variant="body2" color="text.secondary">
                                          • {strength}
                                        </Typography>
                                      </ListItem>
                                    ))}
                                  </List>
                                </Grid>
                              )}
                              
                              {analysis.recommendation.weaknesses?.length > 0 && (
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>
                                    ⚡ Areas for Improvement:
                                  </Typography>
                                  <List dense>
                                    {analysis.recommendation.weaknesses.map((weakness, index) => (
                                      <ListItem key={index} sx={{ py: 0, px: 1 }}>
                                        <Typography variant="body2" color="text.secondary">
                                          • {weakness}
                                        </Typography>
                                      </ListItem>
                                    ))}
                                  </List>
                                </Grid>
                              )}
                            </Grid>

                            {/* Experience Analysis */}
                            {analysis.recommendation.experience && Object.keys(analysis.recommendation.experience).length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="info.main" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>
                                  💼 Experience Analysis:
                                </Typography>
                                <Grid container spacing={1}>
                                  {Object.entries(analysis.recommendation.experience).map(([key, value], index) => (
                                    <Grid item xs={12} sm={6} key={index}>
                                      <Typography variant="body2" color="text.secondary">
                                        <strong>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</strong> {value}
                                      </Typography>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Box>
                            )}
                          </Box>
                        )}

                        {/* Action Result */}
                        {analysis.actionResult && (
                          <Box sx={{ mb: 1, p: 1, bgcolor: analysis.actionResult.success ? 'success.50' : 'error.50', borderRadius: 1 }}>
                            <Typography variant="caption" color={analysis.actionResult.success ? 'success.main' : 'error.main'} sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>
                              Action Taken: {analysis.actionResult.success ? '✅ Success' : '❌ Failed'}
                            </Typography>
                            {analysis.actionResult.message && (
                              <Typography variant="body2" color="text.secondary">
                                {analysis.actionResult.message}
                              </Typography>
                            )}
                          </Box>
                        )}

                        {/* Error Display */}
                        {analysis.error && (
                          <Box sx={{ mb: 1, p: 1, bgcolor: 'error.50', borderRadius: 1 }}>
                            <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold' }}>
                              ❌ Error:
                            </Typography>
                            <Typography variant="body2" color="error.main">
                              {analysis.error}
                            </Typography>
                          </Box>
                        )}

                        <Typography variant="caption" color="text.secondary">
                          🕒 Processed: {formatDate(analysis.timestamp)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {index < candidateAnalyses.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default AutomationStatus; 